import React from 'react'
import PropTypes from 'prop-types'
import Icon from '../../../atoms/Icon'
import Skeleton from '../../../atoms/Skeleton'
import Card from '../../../molecules/Card'

const STATUS_COLORS = {
  errored: { backgroundColor: 'color-mix(in srgb, var(--color-danger-500) 10%, transparent)', color: 'var(--color-danger-500)', border: '1px solid color-mix(in srgb, var(--color-danger-500) 25%, transparent)' },
  processing: { backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 10%, transparent)', color: 'var(--color-primary-500)', border: '1px solid color-mix(in srgb, var(--color-primary-500) 25%, transparent)' },
  idle: { backgroundColor: 'color-mix(in srgb, var(--color-warning-500) 10%, transparent)', color: 'var(--color-warning-500)', border: '1px solid color-mix(in srgb, var(--color-warning-500) 25%, transparent)' },
  complete: { backgroundColor: 'color-mix(in srgb, var(--color-success-500) 10%, transparent)', color: 'var(--color-success-500)', border: '1px solid color-mix(in srgb, var(--color-success-500) 25%, transparent)' }
}

export function getStatusColors (className) {
  return STATUS_COLORS[className] ?? {}
}

export function StatusBadge ({ status }) {
  const colors = getStatusColors(status.className)
  return (
    <span
      className='flex items-center gap-xs radius-md font-size-sm font-bold'
      style={{ padding: '4px 12px', ...colors }}
    >
      <Icon name={status.icon} color={status.iconColor} customSize={14} />
      {status.label}
    </span>
  )
}

StatusBadge.propTypes = {
  status: PropTypes.shape({
    className: PropTypes.string,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    label: PropTypes.string
  }).isRequired
}

export function StatusCell ({ status }) {
  const colors = getStatusColors(status.className)
  return (
    <span
      className='flex items-center gap-xs radius-md font-size-xs font-bold'
      style={{ padding: '3px 10px', color: colors.color, backgroundColor: 'white' }}
    >
      <Icon name={status.icon} color={status.iconColor} customSize={12} />
      {status.label}
    </span>
  )
}

StatusCell.propTypes = {
  status: PropTypes.shape({
    className: PropTypes.string,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    label: PropTypes.string
  }).isRequired
}

export function SummaryCard ({ value, label, backgroundColor, valueColor, selected, dimmed, onClick }) {
  const borderColor = valueColor || 'var(--color-neutral-500)'
  const style = {
    backgroundColor,
    cursor: 'pointer',
    border: selected ? `2px solid ${borderColor}` : '1px solid var(--color-neutral-300)',
    ...(dimmed ? { opacity: 0.5 } : {})
  }
  return (
    <div
      className='text-center py-2 px-3 radius-md'
      style={style}
      role='button'
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
    >
      <div className='font-size-lg font-bold' style={valueColor ? { color: valueColor } : undefined}>{value}</div>
      <div className='font-size-xs'>{label}</div>
    </div>
  )
}

SummaryCard.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  valueColor: PropTypes.string,
  selected: PropTypes.bool,
  dimmed: PropTypes.bool,
  onClick: PropTypes.func.isRequired
}

export function SummaryCardGrid ({ taskSummary, taskFilter, onFilterClick }) {
  return (
    <div className='grid grid-cols-4 gap-md'>
      <SummaryCard
        value={taskSummary.total}
        label='Total Tasks'
        backgroundColor='var(--color-neutral-050)'
        selected={taskFilter === 'all'}
        dimmed={taskFilter !== 'all'}
        onClick={() => onFilterClick('all')}
      />
      <SummaryCard
        value={taskSummary.completed}
        label='Completed'
        backgroundColor='var(--color-success-050)'
        valueColor='var(--color-success-500)'
        selected={taskFilter === 'completed'}
        dimmed={taskFilter !== 'all' && taskFilter !== 'completed'}
        onClick={() => onFilterClick('completed')}
      />
      <SummaryCard
        value={taskSummary.errored}
        label='Errored'
        backgroundColor='var(--color-danger-050)'
        valueColor='var(--color-danger-500)'
        selected={taskFilter === 'errored'}
        dimmed={taskFilter !== 'all' && taskFilter !== 'errored'}
        onClick={() => onFilterClick('errored')}
      />
      <SummaryCard
        value={taskSummary.pending}
        label='Pending'
        backgroundColor='var(--color-neutral-050)'
        valueColor='var(--color-neutral-500)'
        selected={taskFilter === 'pending'}
        dimmed={taskFilter !== 'all' && taskFilter !== 'pending'}
        onClick={() => onFilterClick('pending')}
      />
    </div>
  )
}

SummaryCardGrid.propTypes = {
  taskSummary: PropTypes.shape({
    total: PropTypes.number,
    completed: PropTypes.number,
    errored: PropTypes.number,
    pending: PropTypes.number
  }).isRequired,
  taskFilter: PropTypes.string.isRequired,
  onFilterClick: PropTypes.func.isRequired
}

export function BatchDetailSkeleton ({ metadataCount = 4 }) {
  const metadataItems = Array.from({ length: metadataCount }, (_, i) => (
    <Skeleton key={i} width={i === metadataCount - 1 ? '140px' : (i === 0 ? '120px' : '100px')} height='36px' />
  ))

  return (
    <div className='flex flex-col gap-sm'>
      <Card>
        <div className='flex flex-col gap-md p-md'>
          <div className='flex flex-row gap-md items-center'>
            <Skeleton width='180px' height='28px' />
            <Skeleton width='100px' height='28px' />
          </div>
          <div style={{ borderTop: '1px solid var(--color-neutral-200)' }} className='pt-3'>
            <div className='flex flex-row gap-xl'>
              {metadataItems}
            </div>
          </div>
        </div>
      </Card>
      <div className='grid grid-cols-4 gap-md'>
        <Skeleton height='52px' />
        <Skeleton height='52px' />
        <Skeleton height='52px' />
        <Skeleton height='52px' />
      </div>
    </div>
  )
}

BatchDetailSkeleton.propTypes = {
  metadataCount: PropTypes.number
}
